import React, { useState, useEffect, useRef } from 'react';

const BiomarkerToDisorders = ({ data }) => {
  const [selectedBiomarkers, setSelectedBiomarkers] = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [results, setResults] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const dropdownRef = useRef(null);

  useEffect(() => {
    const uniqueBiomarkers = [...new Set(data.map(item => item.biomarker))].filter(b => b);
    setBiomarkers(uniqueBiomarkers.sort());
  }, [data]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredBiomarkers = biomarkers.filter(biomarker =>
    biomarker.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBiomarkerSelect = (biomarker) => {
    if (!selectedBiomarkers.some(item => item.name === biomarker)) {
      setSelectedBiomarkers(prev => [...prev, { 
        name: biomarker, 
        increase: false, // Default both to false
        decrease: false
      }]);
    }
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const removeBiomarker = (biomarkerToRemove) => {
    setSelectedBiomarkers(prev => prev.filter(b => b.name !== biomarkerToRemove));
  };

  const updateBiomarkerChange = (biomarkerName, changeType, checked) => {
    setSelectedBiomarkers(prev => 
      prev.map(item => 
        item.name === biomarkerName ? { ...item, [changeType]: checked } : item
      )
    );
  };

  const selectAllBiomarkers = () => {
    const biomarkersToSelect = searchTerm ? filteredBiomarkers : biomarkers;
    const newBiomarkers = biomarkersToSelect.filter(biomarker => 
      !selectedBiomarkers.some(item => item.name === biomarker)
    ).map(biomarker => ({ 
      name: biomarker, 
      increase: false,
      decrease: false 
    }));
    
    setSelectedBiomarkers(prev => [...prev, ...newBiomarkers]);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  const clearAllBiomarkers = () => {
    setSelectedBiomarkers([]);
    setSearchTerm('');
  };

  const handleSearch = () => {
    if (selectedBiomarkers.length === 0) {
      alert('Please select at least one biomarker');
      return;
    }

    // Check if at least one biomarker has at least one change type selected
    const hasValidSelection = selectedBiomarkers.some(biomarker => 
      biomarker.increase || biomarker.decrease
    );

    if (!hasValidSelection) {
      alert('Please select at least one change type (Increase or Decrease) for your biomarkers');
      return;
    }

    // Find disorders that contain ALL selected biomarker-change combinations
    const disordersWithAllCombinations = [];

    // Get all unique disorders from data
    const allDisorders = [...new Set(data.map(item => item.disorder))].filter(d => d);

    allDisorders.forEach(disorder => {
      const disorderData = data.filter(item => item.disorder === disorder);
      
      // Check if this disorder has ALL the selected biomarker-change combinations
      const hasAllCombinations = selectedBiomarkers.every(selectedBiomarker => {
        // If neither increase nor decrease is selected, skip this biomarker
        if (!selectedBiomarker.increase && !selectedBiomarker.decrease) {
          return true; // Skip validation for this biomarker
        }
        
        // Check for increase combination if selected
        const hasIncrease = !selectedBiomarker.increase || 
          disorderData.some(item => 
            item.biomarker === selectedBiomarker.name && 
            item.change === 'Increase'
          );
        
        // Check for decrease combination if selected
        const hasDecrease = !selectedBiomarker.decrease || 
          disorderData.some(item => 
            item.biomarker === selectedBiomarker.name && 
            item.change === 'Decrease'
          );
        
        return hasIncrease && hasDecrease;
      });

      if (hasAllCombinations) {
        // Get all biomarkers for this disorder (both selected and non-selected)
        const increasedBiomarkers = disorderData
          .filter(item => item.change === 'Increase')
          .map(item => ({
            biomarker: item.biomarker,
            regions: [item.region]
          }));

        const decreasedBiomarkers = disorderData
          .filter(item => item.change === 'Decrease')
          .map(item => ({
            biomarker: item.biomarker,
            regions: [item.region]
          }));

        // Combine regions for same biomarkers
        const combinedIncreased = increasedBiomarkers.reduce((acc, curr) => {
          const existing = acc.find(item => item.biomarker === curr.biomarker);
          if (existing) {
            existing.regions = [...new Set([...existing.regions, ...curr.regions])];
          } else {
            acc.push({ ...curr, regions: [...new Set(curr.regions)] });
          }
          return acc;
        }, []);

        const combinedDecreased = decreasedBiomarkers.reduce((acc, curr) => {
          const existing = acc.find(item => item.biomarker === curr.biomarker);
          if (existing) {
            existing.regions = [...new Set([...existing.regions, ...curr.regions])];
          } else {
            acc.push({ ...curr, regions: [...new Set(curr.regions)] });
          }
          return acc;
        }, []);

        // Filter to show only selected biomarkers in results
        const filteredIncreased = combinedIncreased.filter(item =>
          selectedBiomarkers.some(b => b.name === item.biomarker && b.increase)
        );

        const filteredDecreased = combinedDecreased.filter(item =>
          selectedBiomarkers.some(b => b.name === item.biomarker && b.decrease)
        );

        disordersWithAllCombinations.push({
          disorder,
          increased: filteredIncreased,
          decreased: filteredDecreased
        });
      }
    });

    setResults(disordersWithAllCombinations);
  };

  const clearResults = () => {
    setResults(null);
    setSelectedBiomarkers([]);
    setSearchTerm('');
  };

  return (
    <div className="search-section">
      <h2>Biomarker to Disorders Mapping</h2>
      
      <div className="search-controls">
        <div className="form-group biomarker-form-group">
          <label>Select Biomarkers with Change Type:</label>
          
          <div className="biomarker-select-container" ref={dropdownRef}>
            <div className="biomarker-input-wrapper">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                placeholder="Type to search biomarkers..."
                className="biomarker-search-input"
              />
              {isDropdownOpen && (
                <div className="biomarker-dropdown">
                  <div className="biomarker-bulk-actions">
                    <button
                      type="button"
                      onClick={selectAllBiomarkers}
                      className="bulk-action-btn"
                    >
                      Select All
                    </button>
                  </div>
                  
                  {filteredBiomarkers.length > 0 ? (
                    filteredBiomarkers.map(biomarker => (
                      <div
                        key={biomarker}
                        className={`biomarker-option ${
                          selectedBiomarkers.some(item => item.name === biomarker) ? 'selected' : ''
                        }`}
                        onClick={() => handleBiomarkerSelect(biomarker)}
                      >
                        <span className="biomarker-name">{biomarker}</span>
                        {selectedBiomarkers.some(item => item.name === biomarker) && (
                          <span className="checkmark">✓</span>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-options">No biomarkers found</div>
                  )}
                </div>
              )}
            </div>

            {selectedBiomarkers.length > 0 && (
              <div className="selected-biomarkers">
                <div className="selected-header">
                  <span>Selected Biomarkers ({selectedBiomarkers.length})</span>
                  <div className="selected-actions">
                    <button 
                      type="button" 
                      onClick={clearAllBiomarkers}
                      className="clear-all-btn"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                <div className="selected-tags-with-change">
                  {selectedBiomarkers.map((biomarker) => (
                    <div key={biomarker.name} className="biomarker-with-change">
                      <div className="biomarker-tag-container">
                        <span className="selected-tag compact">
                          {biomarker.name}
                          <button 
                            type="button" 
                            onClick={() => removeBiomarker(biomarker.name)}
                            className="remove-tag"
                            title="Remove"
                          >
                            ×
                          </button>
                        </span>
                      </div>
                      <div className="change-checkboxes">
                        <label className="checkbox-label increase">
                          <input
                            type="checkbox"
                            checked={biomarker.increase}
                            onChange={(e) => updateBiomarkerChange(biomarker.name, 'increase', e.target.checked)}
                          />
                          <span className="checkmark">↑ Increase</span>
                        </label>
                        <label className="checkbox-label decrease">
                          <input
                            type="checkbox"
                            checked={biomarker.decrease}
                            onChange={(e) => updateBiomarkerChange(biomarker.name, 'decrease', e.target.checked)}
                          />
                          <span className="checkmark">↓ Decrease</span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="button-group">
          <button onClick={handleSearch} className="search-btn">
            Submit
          </button>
          <button onClick={clearResults} className="clear-btn">Clear All</button>
        </div>
      </div>

      <div className="results">
        {results && (
          <div className="comparison-results">
            <h3>Associated Disorders and Biomarkers:</h3>
            {results.length > 0 ? (
              results.map((result, index) => (
                <div key={index} className="disorder-result">
                  <h4 className="disorder-title">• {result.disorder}</h4>
                  
                  {result.increased.length > 0 && (
                    <div className="biomarker-change-group">
                      <h5>Biomarker Increases (↑):</h5>
                      {result.increased.map((item, idx) => (
                        <div key={idx} className="biomarker-region">
                          <strong>{item.biomarker}:</strong> {item.regions.join(', ')}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {result.decreased.length > 0 && (
                    <div className="biomarker-change-group">
                      <h5>Biomarker Decreases (↓):</h5>
                      {result.decreased.map((item, idx) => (
                        <div key={idx} className="biomarker-region">
                          <strong>{item.biomarker}:</strong> {item.regions.join(', ')}
                        </div>
                      ))}
                    </div>
                  )}

                  {result.increased.length === 0 && result.decreased.length === 0 && (
                    <div className="no-biomarkers">
                      No matching biomarker changes found for selected combinations.
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-results">
                <p>No disorders found with all selected biomarker combinations.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BiomarkerToDisorders;